import type { Dayjs } from "dayjs";

import { swSettings } from "app/common/services/swSettings";
import {
    unwantedLanguages,
    ECampaignPagesError,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type {
    ICampaignDataUnion,
    ICampaignsScrapingConfiguration,
    ICampaignsWizardNoDataError,
} from "app/components/campaigns/shared/CampaignsConst";
import { commonWebSources } from "app/components/filters-bar/utils";
import { getParsedDataFromLocalStorage } from "app/components/widget/widget-utilities/Campaigns/CampaignsUtils";
import { translate } from "app/filters/i18nFilter";
import { swNumberFilter } from "app/filters/ngFilters";
import type { IKeywords } from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/adding-keywords-step/keywords-table/BrandMonitoringCampaignTableConsts";
import {
    MAX_CAMPAIGN_NAME_LENGTH,
    isDuplicate,
    MIN_CAMPAIGN_NAME_LENGTH,
} from "app/pages/digital-marketing/brand-monitoring-campaigns/wizard/wizard-pages/create-project-step/BrandMonitoringCampaignsCreateProjecInputValidatorsUtils";
import { getDefaultDurationForRankTrackerCampaign } from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignsContextUtils";
import type {
    IKeywordAndTags,
    RtCampaignScrapeContext,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import { DEFAULT_SELECT_SERP_GROUP } from "app/common/serp/SerpFilterMaps";
import type { RTRawFilterParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import { sanitizeCampaignName } from "app/pages/digital-marketing/keywords-campaigns/wizard/utils";
import { openHooksModalV5 } from "app/services/ModalService";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";
import dayjs from "single-spa/dayjs-extend";
import { proNavigator } from "single-spa/react-app/ProNavigator";

const numberFilter: (a: string | number, b?: number, c?: string) => string = swNumberFilter();
import type { ICampaignsLanguage } from "../searchEngine/SearchEnginesConsts";

export const getLocaleForScrapingConfig = (scrapingConfig: ICampaignsScrapingConfiguration): string => {
    return `${scrapingConfig?.geoLocation}_${scrapingConfig?.locale}`;
};

export const getLocationsIdsForSelectedCampaign = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    device: string,
): string => {
    const locationsIds = scrapingConfigurations
        ?.filter((config) => device?.includes(config.device.toString()))
        .map((scrapingConfig) => getLocaleForScrapingConfig(scrapingConfig))
        .join("&");
    return locationsIds;
};

const getLastAvailableBrandProtectionEtlDate = (): string => {
    return swSettings.components.BrandProtection?.resources?.LastAvailableBrandMonitorEtlDate as string;
};

const campaignToScrapeContext = (campaign: ICampaignDataUnion): RtCampaignScrapeContext => ({
    id: campaign.id,
    scrapingConfigurations: campaign.scrapingConfigurations,
    site: campaign.site,
    createdTime: campaign.createdTime ?? dayjs(),
    firstScrapeTime: "firstScrapeTime" in campaign ? campaign.firstScrapeTime : undefined,
    lastScrapeDate: "lastScrapeDate" in campaign ? campaign.lastScrapeDate : null,
});

export const getDurationForNavigation = (campaign: RtCampaignScrapeContext, isBrandProtection: boolean): string => {
    if (!isBrandProtection) {
        return getDefaultDurationForRankTrackerCampaign(campaign);
    }
    const maxDate = dayjs.utc(getLastAvailableBrandProtectionEtlDate());
    const monthAgo = maxDate.subtract(1, "month");
    const amountOfDays = maxDate.diff(campaign.createdTime, "days");
    return campaign.createdTime.isBefore(monthAgo) ? "1m" : `${Math.max(amountOfDays, 0)}d`;
};

export const getDefaultDevice = (scrapingConfigurations: ICampaignsScrapingConfiguration[]): string => {
    const desktopDeviceId = commonWebSources.desktop().id;
    const isDesktopAvailable = scrapingConfigurations?.some((scrapingConf) =>
        desktopDeviceId.includes(scrapingConf.device.toString()),
    );

    return isDesktopAvailable ? desktopDeviceId : commonWebSources.mobileWeb().id;
};

const clearedFilters: Record<keyof RTRawFilterParams, undefined> = {
    compPosState: undefined,
    competitorsPosition: undefined,
    cpcFromValue: undefined,
    cpcToValue: undefined,
    excludeKeywords: undefined,
    includeKeywords: undefined,
    excludeUrls: undefined,
    includeUrls: undefined,
    intentsFilter: undefined,
    locationsIds: undefined,
    positions: undefined,
    previousPosition: undefined,
    rangeFilter: undefined,
    serpFilterGroup: undefined,
    serpFilterTypes: undefined,
    tags: undefined,
    excludeTags: undefined,
    withUntagged: undefined,
    untaggedFilter: undefined,
    trackOverallPosition: undefined,
    trackedOnly: undefined,
    useCaseFilter: undefined,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    MainSitePositionTrend: undefined,
};

export const navigateToCampaign = (
    campaign: ICampaignDataUnion,
    REPORT_STATE_NAME: string,
    filters: RTRawFilterParams,
    isBrandProtection = false,
) => {
    const scrapeContext = campaignToScrapeContext(campaign);
    const device = getDefaultDevice(scrapeContext.scrapingConfigurations);
    const sites = scrapeContext.site;
    const duration = getDurationForNavigation(scrapeContext, isBrandProtection);
    const graphGranularity = GranularityNames.DAILY;
    const tableGranularity = GranularityNames.DAILY;
    const { graphTab, tableTab, sortedColumn, asc, serpFilterGroup, positionGroup, sort } = proNavigator.getState(
        REPORT_STATE_NAME,
    ).defaultQueryParams as {
        graphTab?: string;
        tableTab?: string;
        sortedColumn?: string;
        asc?: boolean;
        serpFilterGroup?: string;
        positionGroup?: string;
        sort?: string;
    };
    const locationsIds = getLocationsIdsForSelectedCampaign(scrapeContext.scrapingConfigurations, device);
    void proNavigator.go(REPORT_STATE_NAME, {
        // Clear the rest of the filters from the query params
        ...{ ...clearedFilters, ...filters },
        campaignId: scrapeContext.id,
        duration,
        tableGranularity,
        graphGranularity,
        graphTab,
        device,
        sortedColumn,
        asc,
        sort,
        positionGroup,
        locationsIds,
        tableTab,
        sites,
        serpFilterGroup: serpFilterGroup ?? DEFAULT_SELECT_SERP_GROUP,
    });
};

export const getScarpingConfigsForCurrentDevice = (
    scrapingConfigurations: ICampaignsScrapingConfiguration[],
    device: string,
): ICampaignsScrapingConfiguration[] =>
    scrapingConfigurations?.filter((config) => device?.includes(config.device.toString())) ?? [];

export function campaignsFormatNb(numberToFormat: number | undefined): string {
    if (typeof numberToFormat === "undefined") {
        return "";
    }

    const INFINITY_NUMBER = 2147483647; //Number from back office for unlimited number of keywordd or campaignd allowed
    if (numberToFormat === INFINITY_NUMBER) {
        return translate("campaigns.format.number.unlimited");
    }

    return numberFilter(numberToFormat);
}

export const onUpgradeHookClick = (key: string, location?: string) => {
    openHooksModalV5(key, location);
};

export const getWizardNoDataError = (dataItems: ICampaignsWizardNoDataError[]): ECampaignPagesError | null => {
    const error = dataItems.length ? null : ECampaignPagesError.NO_DATA;
    return error;
};

export const getCampaignsNamesFromLocalStorage = (configKey: string): string[] => {
    const campaignsFromLocalStorage: Array<{ name: string }> | undefined =
        getParsedDataFromLocalStorage<Array<{ name: string }>>(configKey) ?? [];
    return campaignsFromLocalStorage.map((campaign) => campaign.name);
};

export const getProjectNameError = (
    projectName: string,
    campaignsList: string[],
    checkMin: boolean,
): ECampaignPagesError | null => {
    const projectNameTrimmed = projectName.trim();
    if (projectNameTrimmed.length > MAX_CAMPAIGN_NAME_LENGTH) {
        return ECampaignPagesError.MAX_LENGTH;
    }
    if (isDuplicate(projectNameTrimmed, campaignsList)) {
        return ECampaignPagesError.DUPLICATE_NAME;
    }
    if (checkMin && projectNameTrimmed?.length < MIN_CAMPAIGN_NAME_LENGTH) {
        return ECampaignPagesError.MINIMUM_LENGTH;
    }

    const { hasIllegalChars, hasBoundaryIssues } = sanitizeCampaignName(projectNameTrimmed);
    if (hasIllegalChars) {
        return ECampaignPagesError.INVALID_CAMPAIGN_NAME;
    } else if (hasBoundaryIssues) {
        return ECampaignPagesError.INVALID_CAMPAIGN_NAME_BOUNDARIES;
    }

    return null;
};

export type IKeywordAndTagsOrIKeywords = IKeywordAndTags | IKeywords;
export const getKeywordsAmount = (
    tableData: IKeywordAndTagsOrIKeywords[],
    checkIfImportedFromModal: (item) => boolean,
): number => {
    const newAmount = tableData.reduce((acc, item) => {
        const isNewKeyword = checkIfImportedFromModal(item);
        if (isNewKeyword) {
            return acc + item.searchEngine.filter((se) => se.isSelected).length;
        } else {
            return acc;
        }
    }, 0);

    return newAmount;
};

export const adjustCreatedTime = (createdTime): Dayjs => {
    // because the scraped data is always one day behind
    return dayjs.utc(createdTime).add(1, "days");
};

export const reorderLanguages = (languages: ICampaignsLanguage[]): ICampaignsLanguage[] => {
    // If the first language in languages is one of the unwantedLanguages, then languages are reordered
    // (all found unwanted languages moved to the end of the languages array); otherwise, the languages
    // are not changed
    if (languages?.length > 1 && unwantedLanguages.includes(languages[0]?.text)) {
        return [
            ...languages.filter((lang) => !unwantedLanguages.includes(lang.text)),
            ...languages.filter((lang) => unwantedLanguages.includes(lang.text)),
        ];
    }
    return languages;
};
